import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { IBreadcrumbItem } from "dash-ui";

import { getCurrentTabFromSearchParam } from "common/helpers";
import { RoutesPath } from "flow-components/routes/routes";

import {
  IBreadcrumbScopeBuildParams,
  useOrganizationsBreadcrumbBuilder,
  usePropertiesBreadcrumbBuilder,
} from "./breadcrumbs-scope-builders";
import { useSaleTeamBreadcrumbBuilder } from "./breadcrumbs-scope-builders/use-sale-team-breadcrumb-builder";
import { useApiBreadcrumbBuilder } from "./breadcrumbs-scope-builders/use-api-breadcrumb-builder";

export const useBreadcrumbBuilder = (): [
  IBreadcrumbItem[],
  (breadcrumbItems: IBreadcrumbItem[]) => void
] => {
  const [getAsyncPropertiesBreadcrumbItems] = usePropertiesBreadcrumbBuilder();
  const [getAsyncOrganizationsBreadcrumbItems] =
    useOrganizationsBreadcrumbBuilder();
  const [getAsyncSaleTeamBreadcrumbItems] = useSaleTeamBreadcrumbBuilder();
  const [getAsyncApiBreadcrumbItems] = useApiBreadcrumbBuilder();

  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>([]);

  const { pathname, search } = useLocation();
  const { t } = useTranslation("common", { keyPrefix: "labels" });
  const currentTab = getCurrentTabFromSearchParam(search);

  // FIXME: re-creating context functions everytime state change. Use useCallback
  useEffect(() => {
    const fetchItems = async () => {
      const buildParams: IBreadcrumbScopeBuildParams = {
        t,
        pathname,
        currentTab,
      };

      let items: IBreadcrumbItem[] = [];

      if (pathname.includes(RoutesPath.Properties)) {
        items = await getAsyncPropertiesBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.Organizations)) {
        items = await getAsyncOrganizationsBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.ExternalSales)) {
        items = await getAsyncSaleTeamBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.MarketAreas)) {
        items = [
          {
            label: t`marketAreas`,
            path: RoutesPath.MarketAreas,
            icon: "mapMarker",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Amenities)) {
        items = [
          {
            label: t`amenities`,
            path: RoutesPath.Amenities,
            icon: "artboard",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Brokers)) {
        items = [
          {
            label: t`brokers`,
            path: RoutesPath.Brokers,
            icon: "checklist",
          },
        ];
      }

      if (pathname.includes(RoutesPath.Management)) {
        items = [
          {
            label: t`management`,
            path: RoutesPath.Management,
            icon: "setting2",
          },
        ];
      }

      if (pathname.includes(RoutesPath.APIManagement)) {
        items = await getAsyncApiBreadcrumbItems(buildParams);
      }

      if (pathname.includes(RoutesPath.Account)) {
        items = [{ label: t`account` }];
      }

      setBreadcrumbItems(items);
    };

    fetchItems().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, t, currentTab]);

  return [breadcrumbItems, (items) => setBreadcrumbItems(items)];
};
